import React, {useState} from 'react'
import {Alert, Button, Modal, Spin, Table, Tooltip} from 'antd'
import {describeOpsError, listOpsTicketsForAsset, readOpsToken} from '../../util/opsApi'
import {EMPTY, formatDate, StatusTag} from '../Asset/OpsTicketDetail'

/**
 * "Pick Ticket" affordance for the IoT Ops Ticket field in the Sub Asset Transfer
 * form. Opens the same Ops tickets (iot-ops) that the Asset History Report shows,
 * scoped to `assetCode` (the Primary Asset for asset->stock, the To Asset otherwise),
 * and lets the user click a ticket to drop its ticketNo into the field.
 *
 * Reuses util/opsApi (separate axios instance, org integration token in
 * X-INTEGRATION-KEY) so no AssetPro JWT is ever sent to Ops.
 */
export default function IotTicketPicker({assetCode, orgId, onSelect, disabled}) {
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [tickets, setTickets] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    setTickets(null)
    try {
      if (!assetCode) {
        setError('Select the asset first — its Ops tickets are looked up by asset code.')
        return
      }
      const tokenResult = await readOpsToken(orgId)
      if (tokenResult.error) {
        setError(tokenResult.error)
        return
      }
      const response = await listOpsTicketsForAsset(tokenResult.token, assetCode)
      if (!Array.isArray(response.data)) {
        setError('Ops returned an unexpected response for the ticket list.')
        return
      }
      setTickets(response.data)
    } catch (e) {
      setError(describeOpsError(e))
    } finally {
      setLoading(false)
    }
  }

  const open = () => {
    setVisible(true)
    load()
  }

  const close = () => setVisible(false)

  const choose = (record) => {
    if (onSelect) onSelect(record.ticketNo)
    setVisible(false)
  }

  const columns = [
    {title: 'Ticket No', dataIndex: 'ticketNo', key: 'ticketNo', render: v => <a>{v || EMPTY}</a>},
    {title: 'Details', dataIndex: 'detailsOfComplaint', key: 'detailsOfComplaint', render: v => v || EMPTY},
    {title: 'Status', dataIndex: 'status', key: 'status', render: s => <StatusTag status={s}/>},
    {title: 'Complaint Date', dataIndex: 'complaintDate', key: 'complaintDate', render: v => formatDate(v)},
    {title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', render: v => formatDate(v, true)},
  ]

  return (
    <React.Fragment>
      <Tooltip title={assetCode ? `Pick an Ops ticket raised for ${assetCode}` : 'Select the asset first to pick a ticket'}>
        <Button size="small" icon="profile" onClick={open} disabled={disabled} style={{marginLeft: 8}}>
          Pick Ticket
        </Button>
      </Tooltip>
      <Modal
        title={`Select IoT Ops Ticket — ${assetCode || 'asset not selected'}`}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={[
          <Button key="refresh" icon="reload" onClick={load} disabled={loading}>Refresh</Button>,
          <Button key="close" onClick={close}>Cancel</Button>,
        ]}
      >
        {error && <Alert type="error" showIcon message={error} style={{marginBottom: 12}}/>}
        {!error && tickets && tickets.length === 0 && !loading && (
          <Alert type="info" showIcon message={`No Ops tickets found for asset code ${assetCode}.`}/>
        )}
        <Spin spinning={loading}>
          {tickets && tickets.length > 0 && (
            <React.Fragment>
              <div style={{marginBottom: 8, color: '#8c8c8c'}}>
                Click a ticket to add its number to the IoT Ops Ticket field.
              </div>
              <Table
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={tickets}
                pagination={{pageSize: 10}}
                scroll={{x: true}}
                onRow={record => ({onClick: () => choose(record), style: {cursor: 'pointer'}})}
              />
            </React.Fragment>
          )}
        </Spin>
      </Modal>
    </React.Fragment>
  )
}
